import { memo, useRef } from 'react';
import FloatAnimation, { FloatAnimationVariant } from './FloatAnimation';
import GlowAnimation from './GlowAnimation';
import RainAnimation from './RainAnimation';

interface IconProps {
  className?: string;
}

// Cloud path used for both clip-path and visual rendering
const CLOUD_PATH =
  'M51.0684 29C63.2823 29 73.1836 38.9013 73.1836 51.1152C73.1836 51.5608 73.1695 52.0033 73.1436 52.4424H80.3457C89.6283 52.4424 97.1543 59.9674 97.1543 69.25C97.1541 78.5325 89.6282 86.0576 80.3457 86.0576H30.8076C21.5251 86.0576 14.0002 78.5325 14 69.25C14 60.5784 20.5669 53.4402 28.999 52.5381C28.9691 52.0676 28.9531 51.5932 28.9531 51.1152C28.9531 38.9012 38.8544 29 51.0684 29Z';

function RainMoonIcon({ className }: IconProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  return (
    <svg
      ref={svgRef}
      width="131"
      height="120"
      viewBox="0 0 131 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-test="weather-icon-rain-moon"
    >
      <g filter="url(#filter0_d_rain_moon)">
        <path
          d="M93.0078 16C89.2514 20.6384 87 26.5457 87 32.9795C87 47.8912 99.0883 59.9795 114 59.9795C114.332 59.9795 114.662 59.9709 114.991 59.959C110.041 66.0709 102.478 69.9795 94 69.9795C79.0883 69.9795 67 57.8912 67 42.9795C67 28.4 78.5558 16.5221 93.0078 16Z"
          fill="#FFA51F"
        >
          <FloatAnimation
            variant={FloatAnimationVariant.MoonDrift}
            rotate
            rotateAmplitude={7}
            rotateDuration={6}
            rotateCenterX={91}
            rotateCenterY={43}
          />
        </path>
      </g>
      {/* Cloud group - foreignObject and path animate together */}
      <g>
        <FloatAnimation />
        {/* Backdrop blur effect using foreignObject with clip-path */}
        <foreignObject x="-6" y="9" width="123.154" height="97.0576">
          <div
            // @ts-expect-error xmlns is valid for foreignObject content
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              clipPath: 'url(#bgblur_rain_moon_clip)',
              height: '100%',
              width: '100%',
            }}
          />
        </foreignObject>
        {/* Cloud overlay with semi-transparent fill */}
        <path d={CLOUD_PATH} fill="white" fillOpacity="0.7" />
      </g>
      <path
        d="M41.1477 81.3516C41.5058 80.6046 42.4017 80.2894 43.1487 80.6475C43.8956 81.0056 44.2108 81.9014 43.8528 82.6484L40.3528 89.9512C39.9947 90.6981 39.0988 91.0133 38.3518 90.6553C37.6049 90.2972 37.2897 89.4013 37.6477 88.6543L41.1477 81.3516Z"
        fill="white"
      >
        <RainAnimation delay={0} />
      </path>
      <path
        d="M56.6477 81.3516C57.0058 80.6046 57.9017 80.2894 58.6487 80.6475C59.3956 81.0056 59.7108 81.9014 59.3528 82.6484L55.8528 89.9512C55.4947 90.6981 54.5988 91.0133 53.8518 90.6553C53.1049 90.2972 52.7897 89.4013 53.1477 88.6543L56.6477 81.3516Z"
        fill="white"
      >
        <RainAnimation delay={0.3} />
      </path>
      <path
        d="M46.1477 92.3516C46.5058 91.6046 47.4017 91.2894 48.1487 91.6475C48.8956 92.0056 49.2108 92.9014 48.8528 93.6484L45.3528 100.951C44.9947 101.698 44.0988 102.013 43.3518 101.655C42.6049 101.297 42.2897 100.401 42.6477 99.6543L46.1477 92.3516Z"
        fill="white"
      >
        <RainAnimation delay={0.6} />
      </path>
      <path
        d="M72.1477 81.3516C72.5058 80.6046 73.4017 80.2894 74.1487 80.6475C74.8956 81.0056 75.2108 81.9014 74.8528 82.6484L71.3528 89.9512C70.9947 90.6981 70.0988 91.0133 69.3518 90.6553C68.6049 90.2972 68.2897 89.4013 68.6477 88.6543L72.1477 81.3516Z"
        fill="white"
      >
        <RainAnimation delay={0.9} />
      </path>
      <path
        d="M61.6477 92.3516C62.0058 91.6046 62.9017 91.2894 63.6487 91.6475C64.3956 92.0056 64.7108 92.9014 64.3528 93.6484L60.8528 100.951C60.4947 101.698 59.5988 102.013 58.8518 101.655C58.1049 101.297 57.7897 100.401 58.1477 99.6543L61.6477 92.3516Z"
        fill="white"
      >
        <RainAnimation delay={1.2} />
      </path>
      <defs>
        <filter
          id="filter0_d_rain_moon"
          x="51"
          y="0"
          width="79.9912"
          height="85.9795"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="8">
            <GlowAnimation />
          </feGaussianBlur>
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 0.852175 0 0 0 0 0.63208 0 0 0 0.8 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        {/* Clip path for backdrop blur - offset to match foreignObject position */}
        <clipPath id="bgblur_rain_moon_clip" transform="translate(6 -9)">
          <path d={CLOUD_PATH} />
        </clipPath>
      </defs>
    </svg>
  );
}

export default memo(RainMoonIcon);
